var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/** @file Contains classes and utilities for processing and analyzing assignment analytics data. */
import moment from 'moment';
import * as stat from 'simple-statistics';
import { Maybe } from '@codegrade/maybe';
import { array, assert, datetime, defaultdict, maybe, object, typeHelpers } from '@codegrade/utils';
import { Assignment } from '@/models';
import { AssignmentsStore, GradersStore, RubricsStore } from '@/store';
import * as utils from '@/utils';
import { CacheDecorator } from '@/utils/cache';
/**
 * Converts a `NaN` number to `null`.
 *
 * @param x The number to check.
 * @returns The number, or `null` if the input was `NaN`.
 */
function numOrNull(x) {
    return Number.isNaN(x) ? null : x;
}
/**
 * Calculates common statistical averages for an array of numbers.
 *
 * @param xs The array of numbers.
 * @returns An object with statistical measures, or `null` if the input array is empty.
 */
function averages(xs) {
    if (xs.length < 1) {
        return null;
    }
    return {
        mean: stat.mean(xs),
        median: stat.median(xs),
        mode: stat.mode(xs),
        stdev: xs.length < 2 ? 0 : stat.sampleStandardDeviation(xs),
    };
}
/** A data source class for processing and analyzing rubric analytics data. */
export class RubricSource {
    workspace;
    data;
    /** The name of this data source as expected by the API. */
    static sourceName = 'rubric_data';
    /** A private cache for memoizing getter results. */
    #cache = new CacheDecorator();
    /** The public accessor for the cache. */
    get _cache() {
        return this.#cache;
    }
    /**
     * Creates a `RubricSource` instance from raw API data.
     *
     * @param workspace The parent analytics workspace.
     * @param data The raw analytics data from the API.
     * @returns A new `RubricSource` instance.
     */
    static fromApiData(workspace, data) {
        if (data.length === 0) {
            return new RubricSource(workspace, Object.freeze({}));
        }
        const rubric = RubricsStore.getRubric()(workspace.assignmentId);
        assert.AssertionError.assert(rubric.isJust(), 'This assignment does not have a rubric');
        const rubricItems = rubric.value.rows.reduce((acc, row) => {
            row.items.forEach(item => {
                acc[item.id] = item;
            });
            return acc;
        }, {});
        const bySub = {};
        for (const sub of data) {
            bySub[sub.workId] = sub.data.rows.map(row => {
                const item = rubricItems[row.itemId];
                const points = item.points;
                if (typeof points !== 'number') {
                    throw new Error('Got item without points');
                }
                return {
                    ...row,
                    points: row.multiplier * points,
                };
            });
        }
        return new RubricSource(workspace, Object.freeze(bySub));
    }
    /**
     * @param workspace The parent analytics workspace.
     * @param data The processed rubric data, keyed by submission ID.
     */
    constructor(workspace, data) {
        this.workspace = workspace;
        this.data = data;
        Object.freeze(this);
    }
    /**
     * Creates a new `RubricSource` containing only data for a subset of submissions.
     *
     * @param subIds A set of submission IDs to filter by.
     * @returns A new, filtered `RubricSource` instance.
     */
    filter(subIds) {
        const data = object.filterObject(this.data, (_, id) => subIds.has(parseInt(id, 10)));
        return new RubricSource(this.workspace, Object.freeze(data));
    }
    /** @returns The rubric model associated with this data. */
    get rubric() {
        const rubric = RubricsStore.getRubric()(this.workspace.assignmentId);
        assert.AssertionError.assert(rubric.isJust(), 'This assignment does not have a rubric');
        return rubric.value;
    }
    /** @returns An array of all row IDs in the rubric. */
    get rowIds() {
        return this.rubric.rows.map(row => row.id);
    }
    /** @returns An array of all row headers (labels) in the rubric. */
    get labels() {
        return this.rubric.rows.map(row => row.header);
    }
    /** @returns A lookup map from rubric item ID to the `RubricItem` object. */
    get rubricItems() {
        return this.rubric.rows.reduce((acc, row) => {
            row.items.forEach(item => {
                acc[item.id] = item;
            });
            return acc;
        }, {});
    }
    /** @returns A lookup map from rubric item ID to its parent `RubricRow` object. */
    get rubricRowPerItem() {
        return this.rubric.rows.reduce((acc, row) => {
            row.items.forEach(item => {
                acc[item.id] = row;
            });
            return acc;
        }, {});
    }
    /** @returns The submitted rubric items, grouped by rubric row (category). */
    get itemsPerCat() {
        const rowPerItem = this.rubricRowPerItem;
        return Object.values(this.data).reduce((acc, items) => {
            items.forEach(item => {
                const rowId = rowPerItem[item.itemId].id;
                acc[rowId].push(item);
            });
            return acc;
        }, object.fromEntries(this.rowIds.map(id => [id, []])));
    }
    /** @returns The number of times each rubric row (category) was filled out. */
    get nTimesFilledPerCat() {
        return object.mapObject(this.itemsPerCat, items => items.length);
    }
    /** @returns The mean score for each rubric row (category). */
    get meanPerCat() {
        return object.mapObject(this.itemsPerCat, items => {
            if (items.length === 0) {
                return null;
            }
            else {
                const points = items.map(item => item.points);
                const mean = stat.mean(points);
                return numOrNull(mean);
            }
        });
    }
    /** @returns The sample standard deviation for each rubric row (category). */
    get stdevPerCat() {
        return object.mapObject(this.itemsPerCat, items => {
            if (items.length < 1) {
                return null;
            }
            else if (items.length === 1) {
                return 0;
            }
            else {
                const points = items.map(item => item.points);
                const stdev = stat.sampleStandardDeviation(points);
                return numOrNull(stdev);
            }
        });
    }
    /** @returns The mode (most common score) for each rubric row (category). */
    get modePerCat() {
        return object.mapObject(this.itemsPerCat, items => {
            if (items.length === 0) {
                return null;
            }
            else {
                const mode = stat.mode(items.map(item => item.points));
                return numOrNull(mode);
            }
        });
    }
    /** @returns The median score for each rubric row (category). */
    get medianPerCat() {
        return object.mapObject(this.itemsPerCat, items => {
            if (items.length === 0) {
                return null;
            }
            else {
                const median = stat.median(items.map(item => item.points));
                return numOrNull(median);
            }
        });
    }
    /** @returns The scores for each submission, grouped by rubric row (category). */
    get scorePerCatPerSubmission() {
        const rowPerItem = this.rubricRowPerItem;
        return object.mapObject(this.data, items => items.reduce((acc, item) => {
            const rowId = rowPerItem[item.itemId].id;
            acc[rowId] = item.points;
            return acc;
        }, {}));
    }
    /** @returns The total rubric score for each submission. */
    get totalScorePerSubmission() {
        return object.mapFilterObject(this.scorePerCatPerSubmission, scorePerCat => {
            const score = stat.sum(Object.values(scorePerCat));
            return Maybe.fromNullable(numOrNull(score));
        });
    }
    /** @returns Data structured for calculating Item-Total Correlation (RIT). */
    get ritItemsPerCat() {
        const scoresPerSub = this.scorePerCatPerSubmission;
        const totalsPerSub = this.totalScorePerSubmission;
        return Object.entries(scoresPerSub).reduce((acc, [subId, scores]) => {
            Object.entries(scores).forEach(([rowId, score]) => {
                if (object.hasAttr(totalsPerSub, subId)) {
                    acc[rowId].push([score, totalsPerSub[subId]]);
                }
            });
            return acc;
        }, object.fromEntries(this.rowIds.map(id => [id, []])));
    }
    /** @returns Data structured for calculating Item-Rest Correlation (RIR). */
    get rirItemsPerCat() {
        return object.mapObject(this.ritItemsPerCat, ritItems => ritItems.map(([score, total]) => [score, total - score]));
    }
    /** @returns The Item-Total Correlation (RIT) for each rubric row (category). */
    get ritPerCat() {
        return object.mapObject(this.ritItemsPerCat, zipped => {
            if (zipped.length < 2) {
                return null;
            }
            const rit = stat.sampleCorrelation(...array.unzip2(zipped));
            return numOrNull(rit);
        });
    }
    /** @returns The Item-Rest Correlation (RIR) for each rubric row (category). */
    get rirPerCat() {
        return object.mapObject(this.rirItemsPerCat, zipped => {
            if (zipped.length < 2) {
                return null;
            }
            const rir = stat.sampleCorrelation(...array.unzip2(zipped));
            return numOrNull(rir);
        });
    }
}
__decorate([
    CacheDecorator.cache
], RubricSource.prototype, "rubricItems", null);
__decorate([
    CacheDecorator.cache
], RubricSource.prototype, "rubricRowPerItem", null);
__decorate([
    CacheDecorator.cache
], RubricSource.prototype, "itemsPerCat", null);
__decorate([
    CacheDecorator.cache
], RubricSource.prototype, "nTimesFilledPerCat", null);
__decorate([
    CacheDecorator.cache
], RubricSource.prototype, "meanPerCat", null);
__decorate([
    CacheDecorator.cache
], RubricSource.prototype, "stdevPerCat", null);
__decorate([
    CacheDecorator.cache
], RubricSource.prototype, "modePerCat", null);
__decorate([
    CacheDecorator.cache
], RubricSource.prototype, "medianPerCat", null);
__decorate([
    CacheDecorator.cache
], RubricSource.prototype, "scorePerCatPerSubmission", null);
__decorate([
    CacheDecorator.cache
], RubricSource.prototype, "totalScorePerSubmission", null);
__decorate([
    CacheDecorator.cache
], RubricSource.prototype, "ritItemsPerCat", null);
__decorate([
    CacheDecorator.cache
], RubricSource.prototype, "rirItemsPerCat", null);
__decorate([
    CacheDecorator.cache
], RubricSource.prototype, "ritPerCat", null);
__decorate([
    CacheDecorator.cache
], RubricSource.prototype, "rirPerCat", null);
/** A data source class for processing and analyzing inline feedback analytics data. */
export class InlineFeedbackSource {
    workspace;
    data;
    /** The name of this data source as expected by the API. */
    static sourceName = 'inline_feedback';
    /** A private cache for memoizing getter results. */
    #cache = new CacheDecorator();
    /** The public accessor for the cache. */
    get _cache() {
        return this.#cache;
    }
    /**
     * Creates an `InlineFeedbackSource` instance from raw API data.
     *
     * @param workspace The parent analytics workspace.
     * @param data The raw analytics data from the API.
     * @returns A new `InlineFeedbackSource` instance.
     */
    static fromApiData(workspace, data) {
        const bySub = {};
        for (const sub of data) {
            bySub[sub.workId] = sub.data;
        }
        return new InlineFeedbackSource(workspace, bySub);
    }
    /**
     * @param workspace The parent analytics workspace.
     * @param data The processed inline feedback data, keyed by submission ID.
     */
    constructor(workspace, data) {
        this.workspace = workspace;
        this.data = data;
        Object.freeze(this);
    }
    /**
     * Creates a new `InlineFeedbackSource` containing only data for a subset of submissions.
     *
     * @param subIds A set of submission IDs to filter by.
     * @returns A new, filtered `InlineFeedbackSource` instance.
     */
    filter(subIds) {
        const data = object.filterObject(this.data, (_, id) => subIds.has(parseInt(id, 10)));
        return new InlineFeedbackSource(this.workspace, data);
    }
    /** @returns Statistical averages for the total number of feedback entries across all submissions. */
    get entryStats() {
        const allEntries = Object.values(this.data);
        return averages(allEntries.map(x => x.totalAmount));
    }
}
__decorate([
    CacheDecorator.cache
], InlineFeedbackSource.prototype, "entryStats", null);
/** A client-side representation of a submission within an analytics workspace. */
export class WorkspaceSubmission {
    workspace;
    /** A tag to identify this class type. */
    tag = 'workspace_submission';
    /** The ID of the submission. */
    id;
    /** The ID of the user who created the submission. */
    userId;
    /** The ID of the user this submission is assigned to for grading. */
    assigneeId;
    /** The raw, unscaled grade of the submission. */
    unscaledGrade;
    /** The creation timestamp of the submission. */
    createdAt;
    /**
     * Creates a `WorkspaceSubmission` from raw API data.
     *
     * @param workspace The parent analytics workspace.
     * @param data The raw analytics data row for a single submission.
     * @returns A new `WorkspaceSubmission` instance.
     */
    static fromApiData(workspace, data) {
        return new WorkspaceSubmission(workspace, data);
    }
    /**
     * @param workspace The parent analytics workspace.
     * @param data The analytics data for this submission.
     */
    constructor(workspace, data) {
        this.workspace = workspace;
        this.id = data.workId;
        this.userId = data.data.userId;
        this.assigneeId = data.data.assigneeId;
        this.unscaledGrade = data.data.grade;
        this.createdAt = data.data.createdAt;
        Object.freeze(this);
    }
    /** @returns The submission's grade, scaled according to the assignment's settings. */
    get grade() {
        return maybe
            .and(this.workspace.assignment, this.unscaledGrade)
            .map(([assignment, unscaledGrade]) => assignment.toScaledGrade(unscaledGrade));
    }
    /** @returns The maximum possible scaled grade for the assignment. */
    get assignmentMaxGrade() {
        return this.workspace.assignment.mapOrDefault(a => a.scaledMaxGrade, 10);
    }
    /**
     * Checks if this submission satisfies all criteria of a given filter.
     *
     * @param filter The filter to check against.
     * @returns `true` if the submission matches the filter.
     */
    satisfies(filter) {
        const { minGrade, maxGrade, submittedAfter, submittedBefore, assignees } = filter;
        return (this.satisfiesGrade(minGrade, maxGrade) &&
            this.satisfiesDate(submittedAfter, submittedBefore) &&
            this.satisfiesAssignees(assignees));
    }
    /**
     * Checks if the submission's grade falls within a given range.
     *
     * @param minGrade The minimum grade (inclusive).
     * @param maxGrade The maximum grade (exclusive, unless it's the max possible grade).
     * @returns `true` if the grade is within the range.
     */
    satisfiesGrade(minGrade, maxGrade) {
        // We do not want a submission to be in both filter A and
        // filter B if A has maxGrade=6 and B has minGrade=6, so we
        // need to check exclusively at one end. However, we need to be
        // inclusive at either 0 or the max grade for this assignment,
        // because otherwise we would drop some submissions. I chose
        // to be inclusive at the minGrade bound and exclusive at the
        // maxGrade bound because that feels more intuitive. This means
        // that a maxGrade of 9 will not contain submissions graded
        // exactly 9, but that a maxGrade of 10 _will_ include
        // submissions graded exactly 10.
        const grade = this.grade.extract();
        if (grade == null) {
            // If grade is `null` we don't want to include it if we filter on
            // `minGrade` or `maxGrade``. In the future we probably want an
            // extra option that allows users to manually filter out submissions
            // without a grade.
            return minGrade == null && maxGrade == null;
        }
        if (minGrade != null && grade < minGrade) {
            return false;
        }
        if (maxGrade != null) {
            const { assignmentMaxGrade } = this;
            if (maxGrade === assignmentMaxGrade && grade === assignmentMaxGrade) {
                return true;
            }
            if (grade >= maxGrade) {
                return false;
            }
        }
        return true;
    }
    /**
     * Checks if the submission's creation date satisfies the date boundaries.
     *
     * @param submittedAfter The start of the date range (inclusive).
     * @param submittedBefore The end of the date range (exclusive).
     * @returns `true` if the creation date is within the range.
     */
    satisfiesDate(submittedAfter, submittedBefore) {
        // Same as with the grade, but we do not have a maximum value to check for.
        return (submittedAfter.mapOrDefault(subAfter => !this.createdAt.isBefore(subAfter), true) &&
            submittedBefore.mapOrDefault(subBefore => this.createdAt.isBefore(subBefore), true));
    }
    /**
     * Checks if the submission's assignee is in the provided list.
     *
     * @param assignees A list of assignees to filter by.
     * @returns `true` if the submission's assignee is in the list, or if the list is empty.
     */
    satisfiesAssignees(assignees) {
        if (assignees.length > 0) {
            return assignees.some(a => a.id === this.assigneeId.extract());
        }
        return true;
    }
}
/** A class that holds and manages a set of `WorkspaceSubmission` objects. */
export class WorkspaceSubmissionSet {
    submissions;
    workspace;
    /**
     * Creates a `WorkspaceSubmissionSet` from raw API data.
     *
     * @param workspace The parent analytics workspace.
     * @param data The raw analytics data for all submissions.
     * @returns A new `WorkspaceSubmissionSet` instance.
     */
    static fromApiData(workspace, data) {
        const submissions = {};
        for (const sub of data) {
            let userSubs = submissions[sub.data.userId];
            if (userSubs == null) {
                userSubs = [];
                submissions[sub.data.userId] = userSubs;
            }
            userSubs.push(WorkspaceSubmission.fromApiData(workspace, sub));
        }
        return new WorkspaceSubmissionSet(submissions, workspace);
    }
    /** A private cache for memoizing getter results. */
    #cache = new CacheDecorator();
    /** The public accessor for the cache. */
    get _cache() {
        return this.#cache;
    }
    /**
     * @param submissions A map from user ID to their submissions.
     * @param workspace The parent analytics workspace.
     */
    constructor(submissions, workspace) {
        this.submissions = submissions;
        this.workspace = workspace;
        object.forEachObject(this.submissions, sub => Object.freeze(sub));
        Object.freeze(this.submissions);
        Object.freeze(this);
    }
    /** @returns A flat array of all submissions from all users. */
    get allSubmissions() {
        return array.flat1(Object.values(this.submissions));
    }
    /** @returns The timestamp of the earliest submission in the set. */
    get firstSubmissionDate() {
        return this.allSubmissions.reduce((first, sub) => {
            if (first == null || first.isAfter(sub.createdAt)) {
                return sub.createdAt;
            }
            else {
                return first;
            }
        }, null);
    }
    /** @returns The timestamp of the latest submission in the set. */
    get lastSubmissionDate() {
        return this.allSubmissions.reduce((last, sub) => {
            if (last == null || last.isBefore(sub.createdAt)) {
                return sub.createdAt;
            }
            else {
                return last;
            }
        }, null);
    }
    /** @returns A `Set` of all unique submission IDs. */
    get submissionIds() {
        return new Set(this.allSubmissions.map(s => s.id));
    }
    /** @returns A `Set` of all unique assignee IDs. */
    get assigneeIds() {
        const assigneeIds = Maybe.mapMaybe(sub => sub.assigneeId, this.allSubmissions);
        return new Set(assigneeIds);
    }
    /**
     * Groups submissions into bins based on a key generated by a given function.
     *
     * @param f A function that takes a submission and returns a key, or `null` to exclude it.
     * @returns A record where keys are the generated bins and values are arrays of submissions.
     */
    binSubmissionsBy(f) {
        return this.allSubmissions.reduce((acc, sub) => {
            const bin = f(sub);
            if (bin != null) {
                acc[bin].push(sub);
            }
            return acc;
        }, defaultdict.defaultdict(() => []));
    }
    /**
     * Groups submissions into bins based on their grade.
     *
     * @param binSize The size of each grade bin.
     * @param maxGrade The maximum possible grade.
     * @returns A record where keys are the bin index and values are arrays of submissions.
     */
    binSubmissionsByGrade(binSize = 1, maxGrade = 10) {
        return this.binSubmissionsBy(sub => sub.grade
            .map(grade => {
            // If the grade is greater than or equal to the max grade (it
            // should only really be possible for it to be equal), we need to
            // adjust it to be placed a bin earlier because there is no
            // separate bin for the maximum grade only.
            if (grade >= maxGrade) {
                const frac = maxGrade / binSize;
                const nbins = Math.ceil(frac);
                const bin = Math.floor(frac);
                return Math.min(bin, nbins - 1);
            }
            return Math.floor(grade / binSize);
        })
            .extractNullable());
    }
    /**
     * Groups submissions into bins based on their creation date.
     *
     * @param range A tuple of start and end date strings.
     * @param binSize The size of each time bin.
     * @param binUnit The unit for the time bin (e.g., 'days', 'weeks').
     * @returns An object mapping a timestamp to the bin's data.
     */
    binSubmissionsByDate(range, binSize, binUnit) {
        const [nullableStart, end] = WorkspaceSubmissionSet.getDateRange(range);
        const binStep = moment.duration(binSize, binUnit).asMilliseconds();
        const start = Maybe.fromNullable(nullableStart)
            .altLazy(() => {
            const firstSub = array.findMin(sub => sub.createdAt.valueOf(), this.allSubmissions);
            return firstSub.map(object.attr('createdAt'));
        })
            .extract();
        if (start == null) {
            return {};
        }
        if (binStep === 0) {
            return {};
        }
        /**
         * Calculates the bin index for a given date.
         *
         * @param d The date.
         * @returns The bin index.
         */
        const getBin = (d) => Math.floor((d.valueOf() - start.valueOf()) / binStep);
        const binned = this.binSubmissionsBy(sub => {
            const { createdAt } = sub;
            if ((start != null && start.isAfter(createdAt)) ||
                (end != null && end.isBefore(createdAt))) {
                return null;
            }
            return getBin(createdAt);
        });
        const binKeys = Object.keys(binned).map(parseFloat);
        if (binKeys.length === 0) {
            return {};
        }
        const first = start == null ? stat.min(binKeys) : getBin(start);
        const last = end == null ? stat.max(binKeys) : getBin(end);
        const result = {};
        for (let i = first; i <= last; i++) {
            let binStart = i * binStep;
            if (start != null) {
                binStart += start.valueOf();
            }
            const binEnd = binStart + binStep - 1;
            result[binStart] = {
                start: binStart,
                end: binEnd,
                data: binned[i],
            };
        }
        return result;
    }
    /**
     * Parses a date range string tuple into start and end `moment` objects.
     *
     * @param range A tuple of start and end date strings.
     * @returns A tuple containing the start and end moments.
     */
    static getDateRange(range) {
        let start;
        let end;
        if (range.length === 0) {
            return [null, null];
        }
        else {
            [start, end] = range.map(d => datetime.toMoment(d));
        }
        if (end == null) {
            end = start.clone();
        }
        return [
            start.clone().local().hours(0).minutes(0).seconds(0).milliseconds(0).utc(),
            end.clone().local().hours(23).minutes(59).seconds(59).milliseconds(999).utc(),
        ];
    }
    /** @returns The total number of submissions in the set. */
    get submissionCount() {
        return this.allSubmissions.length;
    }
    /** @returns The total number of unique students who submitted. */
    get studentCount() {
        return Object.keys(this.submissions).length;
    }
    /** @returns Statistical averages for grades across all submissions. */
    get gradeStats() {
        const grades = object.filterMap(this.allSubmissions, (sub) => sub.grade);
        return averages(grades);
    }
    /** @returns Statistical averages for the number of submissions per student. */
    get submissionStats() {
        const subsPerStudent = Object.values(this.submissions)
            .filter(s => s.length > 0)
            .map(s => s.length);
        return averages(subsPerStudent);
    }
    /** @returns A histogram of submission counts per student. */
    get submissionsPerStudent() {
        const subsPerStudent = Object.values(this.submissions)
            .filter(s => s.length > 0)
            .map(s => s.length);
        return subsPerStudent.reduce((acc, nSubs) => {
            const amt = acc[nSubs];
            acc[nSubs] = amt + 1;
            return acc;
        }, defaultdict.defaultdict(() => 0));
    }
    /**
     * Creates a new, filtered `WorkspaceSubmissionSet`.
     *
     * @param filter The filter to apply.
     * @returns A new `WorkspaceSubmissionSet` containing only the submissions that match the filter.
     */
    filter(filter) {
        let filtered = filter.onlyLatestSubs ? this.getLatestSubmissions() : this.submissions;
        filtered = object.mapObject(filtered, subs => subs.filter(s => s.satisfies(filter)));
        filtered = object.filterObject(filtered, subs => subs.length > 0);
        return new WorkspaceSubmissionSet(filtered, this.workspace);
    }
    /** @returns A map from user ID to an array containing only their latest submission. */
    getLatestSubmissions() {
        return object.mapObject(this.submissions, subs => {
            if (subs.length === 0) {
                return [];
            }
            const [first, ...rest] = subs;
            const latest = rest.reduce((a, b) => (a.createdAt.isAfter(b.createdAt) ? a : b), first);
            return [latest];
        });
    }
}
__decorate([
    CacheDecorator.cache
], WorkspaceSubmissionSet.prototype, "allSubmissions", null);
__decorate([
    CacheDecorator.cache
], WorkspaceSubmissionSet.prototype, "firstSubmissionDate", null);
__decorate([
    CacheDecorator.cache
], WorkspaceSubmissionSet.prototype, "lastSubmissionDate", null);
__decorate([
    CacheDecorator.cache
], WorkspaceSubmissionSet.prototype, "submissionIds", null);
__decorate([
    CacheDecorator.cache
], WorkspaceSubmissionSet.prototype, "assigneeIds", null);
__decorate([
    CacheDecorator.cache
], WorkspaceSubmissionSet.prototype, "gradeStats", null);
__decorate([
    CacheDecorator.cache
], WorkspaceSubmissionSet.prototype, "submissionStats", null);
__decorate([
    CacheDecorator.cache
], WorkspaceSubmissionSet.prototype, "submissionsPerStudent", null);
const WORKSPACE_FILTER_DEFAULT_PROPS = {
    onlyLatestSubs: true,
    minGrade: null,
    maxGrade: null,
    submittedAfter: Maybe.empty(),
    submittedBefore: Maybe.empty(),
    assignees: [],
};
const IWORKSPACE_KEYS_OF = Object.freeze(typeHelpers.keysOfFast(['minGrade', 'maxGrade', 'submittedAfter', 'submittedBefore', 'assignees', 'onlyLatestSubs']));
/**
 * Parses a value into a float, returning `null` if it's not a valid number.
 *
 * @param x The value to parse.
 * @returns The parsed float, or `null`.
 */
function maybeFloat(x) {
    const f = utils.parseOrKeepFloat(x);
    return Number.isNaN(f) ? null : f;
}
/** A class that holds and manages filter criteria for an analytics workspace. */
export class WorkspaceFilter {
    workspace;
    /** A private cache for memoizing getter results. */
    #cache = new CacheDecorator();
    /** The public accessor for the cache. */
    get _cache() {
        return this.#cache;
    }
    /**
     * @param workspace The parent analytics workspace.
     * @param props The raw filter properties to parse.
     */
    constructor(workspace, props) {
        this.workspace = workspace;
        // First check that the given ``props`` object doesn't contain extra
        // keys.
        Object.keys(props).forEach(key => {
            if (!object.hasAttr(WORKSPACE_FILTER_DEFAULT_PROPS, key)) {
                throw new Error(`Invalid filter: ${key}`);
            }
        });
        // Now go over
        IWORKSPACE_KEYS_OF.forEach(key => {
            if (!object.hasAttr(props, key)) {
                const defaultValue = WORKSPACE_FILTER_DEFAULT_PROPS[key];
                this[key] = defaultValue;
            }
            switch (key) {
                case 'minGrade':
                case 'maxGrade':
                    this[key] = maybeFloat(props[key]);
                    break;
                case 'submittedAfter':
                case 'submittedBefore': {
                    const val = props[key];
                    this[key] = Maybe.isMaybe(val) ? val : datetime.toMomentMaybe(val);
                    break;
                }
                case 'assignees': {
                    const assignees = new Map(GradersStore.getGraders()(this.workspace.assignmentId)?.map(a => [a.id, a]));
                    this[key] = (props[key] ?? []).map(assignee => {
                        if (typeof assignee !== 'number') {
                            return assignee;
                        }
                        const user = assignees.get(assignee);
                        assert.AssertionError.assert(user != null, `The specified assignee (${assignee}) was not found`);
                        return user;
                    });
                    break;
                }
                case 'onlyLatestSubs':
                    this[key] = props[key] ?? true;
                    break;
                default:
                    assert.AssertionError.assertNever(key);
            }
        });
        Object.freeze(this);
    }
    /**
     * Creates a new, empty filter for a given workspace.
     *
     * @param workspace The parent analytics workspace.
     * @returns A `WorkspaceFilter` with default values.
     */
    static makeEmpty(workspace) {
        return new WorkspaceFilter(workspace, WORKSPACE_FILTER_DEFAULT_PROPS);
    }
    /**
     * Creates a new `WorkspaceFilter` instance with one property updated.
     *
     * @template T The key of the property to update.
     * @param key The name of the property to update.
     * @param value The new value for the property.
     * @returns A new, updated `WorkspaceFilter` instance.
     */
    update(key, value) {
        return new WorkspaceFilter(this.workspace, {
            // Can't just write `...this`, as `this.workspace` will also be inserted into the object.
            onlyLatestSubs: this.onlyLatestSubs,
            minGrade: this.minGrade,
            maxGrade: this.maxGrade,
            submittedAfter: this.submittedAfter,
            submittedBefore: this.submittedBefore,
            assignees: this.assignees,
            // Overwrite
            [key]: value,
        });
    }
    /**
     * Splits this filter into multiple new filters based on the provided criteria.
     *
     * @param props The criteria to split the filter by.
     * @returns An array of new `WorkspaceFilter` instances.
     */
    split(props) {
        const { minGrade, maxGrade, submittedAfter, submittedBefore } = this;
        const { latest, assignees } = props;
        const grade = utils.parseOrKeepFloat(props.grade);
        // moment(undefined) gives a valid moment, so we must convert it to
        // null first.
        const date = props.date != null ? props.date.orDefault(moment.invalid()) : moment.invalid();
        let result = [this];
        if (!Number.isNaN(grade)) {
            if (minGrade != null && minGrade >= grade) {
                throw new Error('Selected grade is less than or equal to the old "Min grade".');
            }
            if (maxGrade != null && maxGrade <= grade) {
                throw new Error('Selected grade is greater than or equal to the old "Min grade".');
            }
            result = array.flat1(result.map(f => [f.update('maxGrade', grade), f.update('minGrade', grade)]));
        }
        if (date.isValid()) {
            submittedAfter
                .filter(d => !d.isBefore(date))
                .ifJust(() => {
                throw new Error('Selected date is before the old "Submitted after".');
            });
            submittedBefore
                .filter(d => !d.isAfter(date))
                .ifJust(() => {
                throw new Error('Selected date is after the old "Submitted before".');
            });
            result = array.flat1(result.map(f => [
                f.update('submittedBefore', Maybe.of(date)),
                f.update('submittedAfter', Maybe.of(date)),
            ]));
        }
        if (latest) {
            result = array.flat1(result.map(f => [
                f.update('onlyLatestSubs', false),
                f.update('onlyLatestSubs', true),
            ]));
        }
        if (assignees != null && assignees.length > 0) {
            result = array.flat1(assignees.map(a => result.map(f => f.update('assignees', [a]))));
        }
        return result;
    }
    /** @returns A human-readable string representation of the active filters. */
    get _asString() {
        const parts = [];
        if (this.onlyLatestSubs) {
            parts.push('Latest');
        }
        else {
            parts.push('All');
        }
        if (this.minGrade != null && this.maxGrade != null) {
            parts.push(`${this.minGrade} <= Grade < ${this.maxGrade}`);
        }
        else if (this.minGrade != null) {
            parts.push(`Grade >= ${this.minGrade}`);
        }
        else if (this.maxGrade != null) {
            parts.push(`Grade < ${this.maxGrade}`);
        }
        this.submittedAfter.ifJust(subAfter => parts.push(`After ${datetime.readableFormatDate(subAfter)}`));
        this.submittedBefore.ifJust(subBefore => parts.push(`Before ${datetime.readableFormatDate(subBefore)}`));
        parts.push(...this.assignees.map(a => (a.type === 'normal' ? a.name.orDefault('') : a.group.name)));
        return parts.join(', ');
    }
    /** @returns A human-readable string representation of the filter. */
    toString() {
        return this._asString;
    }
    /** @returns A serializable object representing the filter's non-default values. */
    serialize() {
        const defaults = WorkspaceFilter.makeEmpty(this.workspace);
        return IWORKSPACE_KEYS_OF.reduce((acc, key) => {
            if (utils.deepEquals(this[key], defaults[key])) {
                return acc;
            }
            switch (key) {
                case 'submittedAfter':
                case 'submittedBefore': {
                    acc[key] = this[key].map(val => val.toISOString()).extract();
                    break;
                }
                case 'assignees': {
                    const val = this[key];
                    if (val != null) {
                        acc[key] = val.map(a => a.id);
                    }
                    break;
                }
                case 'onlyLatestSubs':
                    acc[key] = this[key];
                    break;
                case 'minGrade':
                    acc[key] = this[key];
                    break;
                case 'maxGrade':
                    acc[key] = this[key];
                    break;
                default:
                    assert.AssertionError.assertNever(key);
            }
            return acc;
        }, {});
    }
    /**
     * Creates a `WorkspaceFilter` instance from serialized data.
     *
     * @param workspace The parent analytics workspace.
     * @param data The serialized filter data.
     * @returns A new `WorkspaceFilter` instance.
     */
    static deserialize(workspace, data) {
        return new WorkspaceFilter(workspace, data);
    }
}
__decorate([
    CacheDecorator.cache
], WorkspaceFilter.prototype, "_asString", null);
/** Represents the result of applying a `WorkspaceFilter` to a `Workspace`. */
export class WorkspaceFilterResult {
    workspace;
    filter;
    /** The filtered set of submissions. */
    submissions;
    /** The filtered inline feedback data. */
    feedbackData;
    /** The filtered rubric data. */
    rubricData;
    /**
     * @param workspace The original `Workspace` to filter.
     * @param filter The `WorkspaceFilter` to apply.
     */
    constructor(workspace, filter) {
        this.workspace = workspace;
        this.filter = filter;
        this.submissions = workspace.submissions.filter(filter);
        const subIds = this.submissions.submissionIds;
        this.feedbackData = workspace.feedbackData.filter(subIds);
        this.rubricData = workspace.rubricData.filter(subIds);
        Object.freeze(this);
    }
}
/** A container for all analytics data and processing logic for a single assignment. */
export class Workspace {
    assignmentId;
    /** The processed submission data. */
    submissions;
    /** The processed inline feedback data. */
    feedbackData;
    /** The processed rubric data. */
    rubricData;
    /**
     * @param assignmentId The ID of the assignment this workspace is for.
     * @param submissionData Raw submission analytics data from the API.
     * @param feedbackData Raw inline feedback analytics data from the API.
     * @param rubricData Raw rubric analytics data from the API.
     */
    constructor(assignmentId, submissionData, feedbackData, rubricData) {
        this.assignmentId = assignmentId;
        this.submissions = WorkspaceSubmissionSet.fromApiData(this, submissionData);
        this.feedbackData = InlineFeedbackSource.fromApiData(this, feedbackData);
        this.rubricData = RubricSource.fromApiData(this, rubricData);
        Object.freeze(this);
    }
    /**
     * @deprecated The store should be the single source of truth. Pass the assignment explicitly if needed.
     * @returns The full assignment model for this workspace.
     */
    get assignment() {
        // We really need an actual assignment model to render grades correctly.
        return AssignmentsStore.getAssignment()(this.assignmentId, {}).filter(a => a instanceof Assignment);
    }
    /**
     * Applies a list of filters to this workspace.
     *
     * @param filters An array of `WorkspaceFilter` instances.
     * @returns An array of `WorkspaceFilterResult` instances, one for each input filter.
     */
    filter(filters) {
        return filters.map(filter => new WorkspaceFilterResult(this, filter));
    }
}
